import React, { useState, useEffect, useRef } from 'react';
import { ArrowLeft } from 'lucide-react';
import { useTranslate } from '../../Hooks/useTranslate';
import { requestOtp, verifyOtp } from '../../userEndApi/AuthApi/OtpApi';
import {
    firebasePhoneLogin,
    firebaseSocialLogin,
    getCountryFlags
} from '../../userEndApi/AuthApi/FirebaseApi';
import { getFirebaseAuth } from '../../utils/firebase';
import FormSelect from '../Custom/FormSelect';
import {
    RecaptchaVerifier,
    signInWithPhoneNumber,
    GoogleAuthProvider,
    FacebookAuthProvider,
    OAuthProvider,
    signInWithPopup
} from "firebase/auth";
import { useAuth } from '../../Context/AuthContext';

const OTP_LENGTH = 6;
const RESEND_SECONDS = 60;

// ── Third-party social icons (circle style) ───────────────────────────────────
const GoogleIcon = () => (
    <svg height="1.5em" style={{ flex: 'none', lineHeight: 1 }} viewBox="0 0 24 24" width="1.5em" xmlns="http://www.w3.org/2000/svg"><title>Google</title><path d="M23 12.245c0-.905-.075-1.565-.236-2.25h-10.54v4.083h6.186c-.124 1.014-.797 2.542-2.294 3.569l-.021.136 3.332 2.53.23.022C21.779 18.417 23 15.593 23 12.245z" fill="#4285F4"></path><path d="M12.225 23c3.03 0 5.574-.978 7.433-2.665l-3.542-2.688c-.948.648-2.22 1.1-3.891 1.1a6.745 6.745 0 01-6.386-4.572l-.132.011-3.465 2.628-.045.124C4.043 20.531 7.835 23 12.225 23z" fill="#34A853"></path><path d="M5.84 14.175A6.65 6.65 0 015.463 12c0-.758.138-1.491.361-2.175l-.006-.147-3.508-2.67-.115.054A10.831 10.831 0 001 12c0 1.772.436 3.447 1.197 4.938l3.642-2.763z" fill="#FBBC05"></path><path d="M12.225 5.253c2.108 0 3.529.892 4.34 1.638l3.167-3.031C17.787 2.088 15.255 1 12.225 1 7.834 1 4.043 3.469 2.197 7.062l3.63 2.763a6.77 6.77 0 016.398-4.572z" fill="#EB4335"></path></svg>
);

const FacebookIcon = () => (
    <svg viewBox="0 0 48 48" width="1.5em" height="1.5em">
        <path fill="#1877F2" d="M48 24C48 10.745 37.255 0 24 0S0 10.745 0 24c0 11.979 8.776 21.908 20.25 23.708V30.938h-6.094V24H20.25v-5.288c0-6.014 3.582-9.337 9.065-9.337 2.625 0 5.372.469 5.372.469v5.906h-3.026c-2.981 0-3.911 1.85-3.911 3.75V24h6.656l-1.064 6.938H27.75v16.77C39.224 45.908 48 35.979 48 24z" />
    </svg>
);

const AppleIcon = () => (
    <svg role="img" width="1.5em" height="1.5em" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701" /></svg>
);

const SocialBtn = ({ icon, label, onClick, disabled }) => (
    <button
        type="button"
        className="otp-social-btn"
        title={label}
        onClick={onClick}
        aria-label={`Continue with ${label}`}
        disabled={disabled}
    >
        {icon}
    </button>
);

// Detect if browser is Safari/Apple
const isApple = /Mac|iPhone|iPad|iPod/i.test(navigator.userAgent || '');

const formSelectCustomStyles = {
    container: (base) => ({
        ...base,
        width: '100px',
        flexShrink: 0,
    }),
    control: (base) => ({
        ...base,
        border: 'none',
        background: 'transparent',
        boxShadow: 'none',
        minHeight: '45px',
        padding: '0',
        cursor: 'pointer',
    }),
    valueContainer: (base) => ({
        ...base,
        padding: '0 8px',
    }),
    indicatorSeparator: () => ({ display: 'none' }),
    dropdownIndicator: (base) => ({
        ...base,
        padding: '0 8px 0 0',
        color: '#666',
        '& svg': {
            width: '16px',
            height: '16px',
        }
    }),
    indicatorsContainer: (base) => ({
        ...base,
        height: '45px',
        alignItems: 'center',
    }),
    menu: (base) => ({
        ...base,
        width: '300px',
        borderRadius: '12px',
        boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
        border: '1px solid rgba(0,0,0,0.05)',
        overflow: 'hidden'
    }),
    option: (base, state) => ({
        ...base,
        backgroundColor: state.isFocused ? 'rgba(197, 160, 89, 0.08)' : 'transparent',
        color: '#333',
        padding: '10px 15px',
        '&:active': {
            backgroundColor: 'rgba(197, 160, 89, 0.15)',
        }
    })
};

const formatCountryOptionLabel = (data, { context }) => {
    if (!data.value) return <span>{data.label}</span>;
    // Debug: log svg_content availability
    if (!data.svg_content) {
        console.warn(`No SVG content for ${data.name} (${data.code})`);
    }
    return (
        <div className="d-flex align-items-center gap-2">
            {data.svg_content ? (
                <div
                    className="country-flag-icon"
                    dangerouslySetInnerHTML={{ __html: data.svg_content }}
                    style={{ width: context === 'value' ? '20px' : '22px', height: 'auto', flexShrink: 0, display: 'flex', alignItems: 'center' }}
                />
            ) : (
                <span style={{ fontSize: '1.2rem' }}>{data.emoji || '🏳️'}</span>
            )}
            <span style={{ fontSize: context === 'value' ? '0.75rem' : '0.9rem', fontWeight: context === 'value' ? '600' : '400' }}>
                {context === 'value' ? data.dial_code : `${data.name} (${data.dial_code})`}
            </span>
            {context === 'menu' && <span className="ms-auto text-muted small" style={{ fontSize: '0.75rem' }}>{data.code}</span>}
        </div>
    );
};

// ── Main component ────────────────────────────────────────────────────────────
const OtpAuthForm = ({ onSuccess, onLoading }) => {
    const { t } = useTranslate();
    const { login, fetchUserData } = useAuth();

    // 'email' | 'mobile' | 'otp'
    const [mode, setMode] = useState('email');
    const [identifier, setIdentifier] = useState('');
    const [countryCode, setCountryCode] = useState('+91');
    const [maxLength, setMaxLength] = useState(10);
    const [otp, setOtp] = useState(Array(OTP_LENGTH).fill(''));
    const [countdown, setCountdown] = useState(0);
    const otpRefs = useRef([]);

    const [loading, setLoading] = useState(false);
    const [countries, setCountries] = useState([]);
    const [loadingCountries, setLoadingCountries] = useState(false);
    const [error, setError] = useState('');
    const [infoMessage, setInfoMessage] = useState('');

    // Sync loading state with parent
    useEffect(() => {
        if (onLoading) onLoading(loading);
    }, [loading, onLoading]);

    // Safety timeout: reset loading if stuck for more than 30 seconds
    useEffect(() => {
        if (!loading) return;
        const timeout = setTimeout(() => {
            console.log("Safety timeout: Resetting stuck loading state");
            setLoading(false);
        }, 30000); // 30 seconds max
        return () => clearTimeout(timeout);
    }, [loading]);

    // Detect when user returns from popup (window regains focus) and reset loading
    useEffect(() => {
        if (!loading) return;
        const handleFocus = () => {
            // Small delay to check if error was already handled
            setTimeout(() => {
                console.log("Window regained focus - resetting loading state");
                setLoading(false);
                setError(''); // Clear any error
                setInfoMessage(''); // Clear info message
            }, 300);
        };
        window.addEventListener('focus', handleFocus);
        return () => window.removeEventListener('focus', handleFocus);
    }, [loading]);

    // Firebase Specific Refs
    const confirmationResult = useRef(null);
    const recaptchaVerifier = useRef(null);

    // Fetch countries for mobile selector
    useEffect(() => {
        const fetchCountries = async () => {
            setLoadingCountries(true);
            try {
                const res = await getCountryFlags();
                if (res.success && res.data && Array.isArray(res.data.country_flags)) {
                    const formattedCountries = res.data.country_flags.map(c => ({
                        ...c,
                        value: c.dial_code,
                        label: `${c.name} (${c.dial_code})`
                    }));
                    setCountries(formattedCountries);
                    const defaultCountry = res.data.default_country;
                    const india = res.data.country_flags.find(c => c.dial_code === '+91');

                    if (defaultCountry && defaultCountry.dial_code) {
                        setCountryCode(defaultCountry.dial_code);
                        setMaxLength(defaultCountry.maxlength || 10);
                    } else if (india) {
                        setCountryCode(india.dial_code);
                        setMaxLength(india.maxlength || 10);
                    } else if (res.data.country_flags.length > 0) {
                        setCountryCode(res.data.country_flags[0].dial_code);
                        setMaxLength(res.data.country_flags[0].maxlength || 10);
                    }
                }
            } catch (err) {
                console.error("Failed to load countries", err);
            } finally {
                setLoadingCountries(false);
            }
        };
        fetchCountries();
    }, []);

    useEffect(() => {
        if (countdown <= 0) return;
        const timer = setTimeout(() => setCountdown(c => c - 1), 1000);
        return () => clearTimeout(timer);
    }, [countdown]);

    // Cleanup Recaptcha on unmount
    useEffect(() => {
        return () => {
            if (recaptchaVerifier.current) {
                recaptchaVerifier.current.clear();
            }
        };
    }, []);

    const switchMode = (newMode) => {
        setMode(newMode);
        setIdentifier('');
        setError('');
        setInfoMessage('');
        if (recaptchaVerifier.current) {
            recaptchaVerifier.current.clear();
            recaptchaVerifier.current = null;
        }
    };

    // Detect if browser is Safari
    // const isApple = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

    // ── Social Login ──────────────────────────────────────────────────────────
    const handleSocialLogin = async (providerName) => {
        setLoading(true); setError(''); setInfoMessage('');

        // Clear any existing recaptcha verifier to avoid interference
        if (recaptchaVerifier.current) {
            recaptchaVerifier.current.clear();
            recaptchaVerifier.current = null;
        }

        try {
            const auth = await getFirebaseAuth();
            let provider;

            if (providerName === 'google') {
                provider = new GoogleAuthProvider();
                // Request email scope explicitly
                provider.addScope('email');
                provider.addScope('profile');
                provider.setCustomParameters({
                    prompt: 'select_account'
                });
            } else if (providerName === 'facebook') {
                provider = new FacebookAuthProvider();
                // Request email permission
                provider.addScope('email');
                provider.setCustomParameters({
                    auth_type: 'reauthenticate'
                });
            } else if (providerName === 'apple') {
                provider = new OAuthProvider('apple.com');
                // Request email scope for Apple
                provider.addScope('email');
                provider.addScope('name');
            } else {
                throw new Error("Provider not supported yet");
            }

            const result = await signInWithPopup(auth, provider);
            const idToken = await result.user.getIdToken();

            // Send idToken to backend
            const loginResult = await firebaseSocialLogin(idToken);

            if (loginResult?.success) {
                login(loginResult.data.user);
                // Fetch latest user data from backend after successful login
                await fetchUserData();
                onSuccess();
            } else if (loginResult?.code === 'USER_CANCELLED') {
                // User cancelled - show friendly info message
                console.log("User cancelled login at backend");
                setInfoMessage('Login cancelled. You can try again whenever you\'re ready.');
                setError('');
            } else {
                setError(loginResult?.message || "Social login failed at backend");
            }
        } catch (err) {
            console.log("Social login error caught:", err?.code, err?.message);

            // Handle popup closed or cancelled - reset loading silently
            const popupErrors = [
                'auth/popup-closed-by-user',
                'auth/cancelled-popup-request',
                'auth/popup-blocked',
                'auth/network-request-failed',
                'auth/user-cancelled'
            ];

            // User-friendly error messages
            const errorMessages = {
                'auth/user-cancelled': 'Login cancelled. You can try again whenever you\'re ready.',
                'auth/popup-closed-by-user': 'Login cancelled. Please try again.',
                'auth/popup-blocked': 'Popup was blocked. Please allow popups for this site.',
                'auth/account-exists-with-different-credential': 'An account already exists with this email using a different login method.'
            };

            if (popupErrors.includes(err?.code)) {
                console.log("Popup closed or cancelled - resetting state");
                setInfoMessage('Login cancelled. You can try again whenever you\'re ready.');
                setError(''); // Clear any error
            } else if (errorMessages[err?.code]) {
                setError(errorMessages[err?.code]);
            } else {
                console.error("Social Login Error:", err);
                setError(err?.message || "Social login failed. Please try again.");
            }
        } finally {
            console.log("Resetting loading state");
            setLoading(false);
        }
    };

    // ── Send OTP ──────────────────────────────────────────────────────────────
    const handleSend = async (e) => {
        if (e && e.preventDefault) e.preventDefault();

        if (!identifier.trim()) {
            setError(mode === 'email'
                ? t('email_required', 'Please enter your email address')
                : t('phone_required', 'Please enter your mobile number'));
            return;
        }

        setLoading(true); setError(''); setInfoMessage('');

        try {
            const fullIdentifier = mode === 'mobile' ? `${countryCode}${identifier.trim()}` : identifier.trim();

            if (mode === 'mobile') {
                // ── Firebase Mobile Flow ──────────────────────────────────────
                const auth = await getFirebaseAuth();

                // Initialize invisible recaptcha if not already done
                if (!recaptchaVerifier.current) {
                    recaptchaVerifier.current = new RecaptchaVerifier(auth, 'recaptcha-container', {
                        size: 'invisible',
                        callback: () => { /* reCAPTCHA solved */ }
                    });
                }

                const result = await signInWithPhoneNumber(auth, fullIdentifier, recaptchaVerifier.current);
                confirmationResult.current = result;
                setMode('otp');
            } else {
                // ── Backend Email Flow ────────────────────────────────────────
                await requestOtp({ identifier: fullIdentifier });
                setMode('otp');
            }

            setCountdown(RESEND_SECONDS);
            setTimeout(() => otpRefs.current[0]?.focus(), 100);
        } catch (err) {
            console.error("Auth Error:", err);
            let msg = err.message || "Failed to send OTP";
            if (err.code === 'auth/invalid-phone-number') msg = "Invalid phone number format (use + country code)";
            if (err.code === 'auth/too-many-requests') msg = "Too many requests. Please try again later.";
            setError(msg);

            // Reset recaptcha on error
            if (recaptchaVerifier.current) {
                recaptchaVerifier.current.clear();
                recaptchaVerifier.current = null;
            }
        } finally {
            setLoading(false);
        }
    };

    // ── OTP input ─────────────────────────────────────────────────────────────
    const handleOtpChange = (index, value) => {
        const digit = value.replace(/\D/g, '').slice(-1);
        const newOtp = [...otp]; newOtp[index] = digit; setOtp(newOtp); setError(''); setInfoMessage('');
        if (digit && index < OTP_LENGTH - 1) otpRefs.current[index + 1]?.focus();
    };

    const handleOtpKeyDown = (index, e) => {
        if (e.key === 'Backspace' && !otp[index] && index > 0) otpRefs.current[index - 1]?.focus();
    };

    const handleOtpPaste = (e) => {
        e.preventDefault();
        const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, OTP_LENGTH);
        const newOtp = Array(OTP_LENGTH).fill('');
        pasted.split('').forEach((d, i) => { newOtp[i] = d; });
        setOtp(newOtp);
        otpRefs.current[Math.min(pasted.length, OTP_LENGTH - 1)]?.focus();
    };

    // ── Verify OTP ────────────────────────────────────────────────────────────
    const handleVerify = async (e) => {
        e.preventDefault();
        const otpValue = otp.join('');
        if (otpValue.length < OTP_LENGTH) { setError(t('otp_incomplete', `Please enter all ${OTP_LENGTH} digits`)); return; }
        setLoading(true); setError(''); setInfoMessage('');

        try {
            const fullIdentifier = mode === 'otp' && confirmationResult.current
                ? `${countryCode}${identifier.trim()}`
                : identifier.trim();

            if (mode === 'otp' && confirmationResult.current) {
                // ── Firebase Verification ─────────────────────────────────────
                const firebaseResult = await confirmationResult.current.confirm(otpValue);
                const idToken = await firebaseResult.user.getIdToken();

                // Verify token with backend to establish session
                const result = await firebasePhoneLogin(idToken);
                if (result?.success) {
                    login(result.data.user);
                    // Fetch latest user data from backend after successful login
                    await fetchUserData();
                    onSuccess();
                } else {
                    setError(result?.message || "Failed to establish session");
                }
            } else {
                // ── Backend Email Verification ────────────────────────────────
                const result = await verifyOtp({ identifier: fullIdentifier, otp: otpValue });
                if (result?.success) {
                    login(result.data.user);
                    // Fetch latest user data from backend after successful login
                    await fetchUserData();
                    onSuccess();
                } else {
                    setError(result?.message || t('otp_invalid', 'Invalid or expired OTP'));
                }
            }
        } catch (err) {
            console.error("Verification Error:", err);
            let msg = err.message || t('verification_failed', 'Verification failed');
            if (err.code === 'auth/invalid-verification-code') msg = "Invalid verification code. Please check and try again.";
            if (err.code === 'auth/code-expired') msg = "Code has expired. Please request a new one.";
            setError(msg);
        } finally {
            setLoading(false);
        }
    };

    const handleResend = async () => {
        if (countdown > 0) return;
        setLoading(true); setError(''); setInfoMessage(''); setOtp(Array(OTP_LENGTH).fill(''));
        try {
            const fullIdentifier = confirmationResult.current ? `${countryCode}${identifier.trim()}` : identifier.trim();

            if (confirmationResult.current) {
                // Resend for Firebase
                const auth = await getFirebaseAuth();
                const result = await signInWithPhoneNumber(auth, fullIdentifier, recaptchaVerifier.current);
                confirmationResult.current = result;
            } else {
                await requestOtp({ identifier: fullIdentifier });
            }
            setCountdown(RESEND_SECONDS);
            setTimeout(() => otpRefs.current[0]?.focus(), 100);
        } catch (err) { setError(err.message); }
        finally { setLoading(false); }
    };

    // ── Email / Mobile input step ─────────────────────────────────────────────
    if (mode === 'email' || mode === 'mobile') {
        const isEmail = mode === 'email';
        return (
            <div className="otp-auth-form">
                <form onSubmit={handleSend} className="auth-form" noValidate>
                    {error && <div className="alert alert-danger">{error}</div>}
                    {infoMessage && <div className="alert alert-info">{infoMessage}</div>}

                    <div className="form-group">
                        <label>{isEmail ? t('email', 'Email') : t('mobile', 'Mobile Number')}</label>
                        <div className={`otp-input-wrapper ${!isEmail ? 'has-country-code' : ''}`}>
                            {!isEmail && (
                                <FormSelect
                                    noLabel
                                    field={{ name: 'countryCode' }}
                                    placeholder="Country Name or Code"
                                    value={countryCode}
                                    handleChange={(e) => {
                                        const selected = countries.find(c => c.dial_code === e.target.value);
                                        setCountryCode(e.target.value);
                                        setMaxLength(selected?.maxlength);
                                    }}
                                    options={countries}
                                    formatOptionLabel={formatCountryOptionLabel}
                                    customStyles={formSelectCustomStyles}
                                />
                            )}
                            <input
                                key={mode}
                                type={isEmail ? 'email' : 'tel'}
                                inputMode={isEmail ? 'email' : 'numeric'}
                                value={identifier}
                                onChange={(e) => {
                                    let val = e.target.value;
                                    if (!isEmail) {
                                        val = val.replace(/\D/g, '');
                                    }
                                    setIdentifier(val);
                                    setError('');
                                    setInfoMessage('');
                                }}
                                placeholder={isEmail
                                    ? t('enter_email_address', 'Enter Email Address')
                                    : t('enter_mobile_number', 'Mobile Number')}
                                maxLength={isEmail ? undefined : maxLength}
                                className={`form-control ${error ? 'is-invalid' : ''}`}
                                autoFocus
                                autoComplete={isEmail ? 'email' : 'tel'}
                            />
                        </div>
                    </div>

                    <button type="submit" className="btn btn-primary btn-block otp-continue-btn" disabled={loading}>
                        {loading
                            ? <span className="spinner-border spinner-border-sm" />
                            : t('continue', 'Continue')}
                    </button>
                </form>

                <div className="otp-switch-mode">
                    {isEmail ? (
                        <button type="button" className="otp-switch-btn" onClick={() => switchMode('mobile')}>
                            {t('login/signup_with_mobile', 'Login/Signup with Mobile')}
                        </button>
                    ) : (
                        <button type="button" className="otp-switch-btn" onClick={() => switchMode('email')}>
                            {t('login/signup_with_email', 'Login/Signup with Email')}
                        </button>
                    )}
                </div>

                <div className="auth-footer">
                    <div className="auth-divider">
                        <span>{t('or_login_with', 'Or Login/Signup With')}</span>
                    </div>
                    <div className="otp-social-group">
                        <SocialBtn
                            icon={<GoogleIcon />}
                            label="Google"
                            onClick={() => handleSocialLogin('google')}
                            disabled={loading}
                        />
                        <SocialBtn
                            icon={<FacebookIcon />}
                            label="Facebook"
                            onClick={() => handleSocialLogin('facebook')}
                            disabled={loading}
                        />
                        {isApple && (
                            <SocialBtn
                                icon={<AppleIcon />}
                                label="Apple"
                                onClick={() => handleSocialLogin('apple')}
                                disabled={loading}
                            />
                        )}
                    </div>
                </div>

                <p className="otp-terms-note">
                    {t('by_proceeding', 'By proceeding, you agree to Renbow Resort\'s')}{' '}
                    <a href="/privacy-policy" className="otp-terms-link">{t('privacy_policy', 'Privacy Policy')}</a>,{' '}
                    <a href="/terms-conditions" className="otp-terms-link">{t('user_agreement', 'User Agreement')}</a>{' '}
                    {t('and', 'and')}{' '}
                    <a href="/terms-conditions" className="otp-terms-link">{t('tnc', 'T&Cs')}</a>
                </p>

                {/* Fixed container for Firebase ReCAPTCHA - stays in DOM to prevent timeouts */}
                <div id="recaptcha-container" style={{ visibility: 'hidden', height: 0, overflow: 'hidden' }}></div>
            </div>
        );
    }

    // ── OTP verification step ─────────────────────────────────────────────────
    return (
        <div className="otp-auth-form">
            <div className="auth-header">
                <button className="auth-back-btn"
                    onClick={() => { setMode('email'); setError(''); setInfoMessage(''); setOtp(Array(OTP_LENGTH).fill('')); confirmationResult.current = null; }}>
                    <ArrowLeft size={14} /> {t('back', 'Back')}
                </button>
                <h1>{t('verify_otp', 'Verify Code')}</h1>
                <p>
                    {t('otp_sent_to', 'We sent a 6-digit code to')}<br />
                    <strong className="auth-identifier">{identifier}</strong>
                </p>
            </div>

            <form onSubmit={handleVerify} className="auth-form" noValidate>
                {error && <div className="alert alert-danger">{error}</div>}
                {infoMessage && <div className="alert alert-info">{infoMessage}</div>}

                <div className="otp-input-group" onPaste={handleOtpPaste}>
                    {otp.map((digit, i) => (
                        <input
                            key={i}
                            ref={(el) => { otpRefs.current[i] = el; }}
                            type="text" inputMode="numeric" maxLength={1}
                            value={digit}
                            onChange={(e) => handleOtpChange(i, e.target.value)}
                            onKeyDown={(e) => handleOtpKeyDown(i, e)}
                            className={`otp-box ${error ? 'is-invalid' : digit ? 'filled' : ''}`}
                            aria-label={`Digit ${i + 1}`}
                        />
                    ))}
                </div>

                <button type="submit" className="btn btn-primary btn-block mt-3"
                    disabled={loading || otp.join('').length < OTP_LENGTH}>
                    {loading
                        ? <span className="spinner-border spinner-border-sm" />
                        : t('verify_continue', 'Verify & Continue')}
                </button>

                <div className="auth-resend">
                    {countdown > 0
                        ? <span className="text-muted">{t('resend_in', 'Resend in')} <strong>{countdown}s</strong></span>
                        : <button type="button" className="auth-link" onClick={handleResend} disabled={loading}>
                            {t('resend_otp', "Didn't receive it? Resend")}
                        </button>
                    }
                </div>
            </form>

            {/* Fixed container for Firebase ReCAPTCHA - duplicated here to ensure it's ALWAYS in DOM */}
            <div id="recaptcha-container" style={{ visibility: 'hidden', height: 0, overflow: 'hidden' }}></div>
        </div>
    );
};

export default OtpAuthForm;
